// The saloon's live dressing on top of the baked room: the gun case (the posse's actual weapons,
// stood in the trough and hung on brass pegs, each with a brass nameplate, under a row of recessed
// downlights; whatever you have equipped sits in a brighter, warmer light), the notice board with the
// run's recall notice and the robots on the books, and Pop's faded poster behind the bar
// (STORY_BIBLE §1.8 / §6).
//
// Guns come from the "Weapons and loadout" lane (client/weapons/guns.ts) when it's on this branch,
// through import.meta.glob so the saloon builds either way; until then, placeholder long guns.
// Unlocks don't exist yet: `rackWeapons.ids` is the hook for them (default: every gun in the book).

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { CAR } from '../../sim/saloon';
import { drawEmblem } from './emblems';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const POSTERS = BASE + 'assets/saloon/posters/';

/** Which weapon ids hang on the rack (an unlock system can set this). Null = every primary/sidearm. */
export const rackWeapons: { ids: (() => number[]) | null } = { ids: null };

// the case, car-local (build_saloon.py rack(): the cabinet, the felt trough, the crown). Inside it
// everything is live: car.ts clearRack folds the baked sconce, pegs and rail out of RACK_CLEAR.
const HW = CAR.hw;
const CZ0 = CAR.rackZ - CAR.rackHalf + 0.05, CZ1 = CAR.rackZ + CAR.rackHalf - 0.05; // between the stiles
const BOARD_X = HW - 0.125; // the back board, in front of the baked back panel and pilaster
const LONG_X = HW - 0.26, PEG_X = HW - 0.24;
const TROUGH_Y = 1.0, PEG_Y = 2.0, SOFFIT_Y = 2.27, CROWN_Y = 2.35;
const NADE_Y = 0.6;
/** five sidearm columns, the four long guns between them */
const pegZ = (i: number) => CAR.rackZ - 0.96 + i * 0.48;
const longZ = (i: number) => CAR.rackZ - 0.72 + i * 0.48;
const PUCK_X = (HW - 0.42 + BOARD_X) / 2;
export const RACK_CLEAR = [
  // the trough dividers (the felt runs a hair further, so it stays)
  new THREE.Box3(new THREE.Vector3(HW - 0.39, 0.94, CZ0), new THREE.Vector3(HW + 0.01, 1.05, CZ1)),
  // the rail, its notches, the pegs, the pilaster sconce and its bulb
  new THREE.Box3(new THREE.Vector3(HW - 0.27, 1.05, CZ0 - 0.02), new THREE.Vector3(HW + 0.01, 2.33, CZ1 + 0.02)),
];
/** the case downlight, as the guns' key for their rim and gleam: from above, a touch toward the aisle */
const CASE_LIGHT = { value: new THREE.Vector3(-0.35, 1, 0).normalize() };
const WARM = new THREE.Color(1.0, 0.78, 0.5), GOLD = new THREE.Color(1.0, 0.66, 0.3);
const nadeZ = (i: number) => CAR.rackZ - CAR.rackHalf + 0.255 + i * 0.21;
/** the bandolier's element bands, by grenade id (sim/weapons.ts G): impact brass, fire, ice, shock, acid,
 *  magnetic, shrapnel iron, stun white, gas green, time teal */
const NADE_COLOR = [0xd8b060, 0xff7a2a, 0x7fd6ff, 0xf3e65a, 0x9be03a, 0xb07aff, 0x9a8e80, 0xf4f1e8, 0x86b04a, 0x5ad1c8];
const itemKey = (kind: string, id: number) => kind + ':' + id;

interface GunsMod {
  buildWeaponMesh(id: number): THREE.Object3D;
  buildCaseWeapon?(id: number, light: { value: THREE.Vector3 }): { obj: THREE.Object3D; shine(k: number): void };
}
interface WeaponsMod {
  WEAPONS: { id: number; name: string; short: string; slot: number; pistol: boolean; proj: number }[];
}
const gunMods = import.meta.glob('../weapons/guns.ts');
const weaponMods = import.meta.glob('../../sim/weapons.ts');

/** Room light on unlit props: what the bake would have given them. */
const ROOM = new THREE.Color(0.95, 0.82, 0.66);

export class SaloonProps {
  readonly group = new THREE.Group();
  private loader = new THREE.TextureLoader();
  private rack = new THREE.Group();

  constructor() {
    this.group.name = 'saloon-props';
    this.group.add(this.rack);
    this.posters();
    this.clock();
    this.caseShell();
    this.bandolier();
    this.plaques();
    void this.fillRack();
  }

  private hands: THREE.Object3D[] = [];

  /** The ref's round clock over the fore door. Company time: it runs, and it's never late. */
  private clock() {
    const c = document.createElement('canvas');
    c.width = c.height = 256;
    const g = c.getContext('2d')!;
    const grad = g.createRadialGradient(128, 128, 20, 128, 128, 128);
    grad.addColorStop(0, '#efe3c4'); grad.addColorStop(1, '#cdb88a');
    g.fillStyle = grad; g.beginPath(); g.arc(128, 128, 126, 0, Math.PI * 2); g.fill();
    g.fillStyle = '#2a1a0a'; g.font = 'bold 30px Georgia, serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
    const roman = ['XII', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI'];
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      g.font = `bold ${i % 3 === 0 ? 28 : 20}px Georgia, serif`;
      g.fillText(roman[i], 128 + Math.sin(a) * 96, 128 - Math.cos(a) * 96);
    }
    g.font = 'bold 13px Georgia, serif'; g.fillText('G.U.L.P.', 128, 168);
    g.font = '9px Georgia, serif'; g.fillText('EVERYTHING MUST GO', 128, 184);
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
    const clock = new THREE.Group();
    clock.position.set(0, 2.78, -CAR.hl + 0.06);
    const face = new THREE.Mesh(new THREE.CircleGeometry(0.2, 40), new THREE.MeshBasicMaterial({ map: tex, color: ROOM }));
    const brass = new THREE.MeshStandardMaterial({ color: 0xb08440, metalness: 1, roughness: 0.3 });
    const rim = new THREE.Mesh(new THREE.TorusGeometry(0.21, 0.022, 10, 48), brass);
    const ink = new THREE.MeshBasicMaterial({ color: 0x1a120a });
    for (const [len, w] of [[0.11, 0.012], [0.16, 0.008]] as const) {
      const h = new THREE.Group();
      const bar = new THREE.Mesh(new THREE.PlaneGeometry(w, len), ink);
      bar.position.set(0, len / 2 - 0.015, 0.004);
      h.add(bar); this.hands.push(h); clock.add(h);
    }
    clock.add(face, rim);
    this.group.add(clock);
  }

  update(dt = 1 / 60) {
    this.t += dt;
    this.glow(dt);
    // the browsed rack item comes 6 cm off the wall toward you
    for (const [k, it] of this.items) {
      const want = k === this.focusKey ? 1 : 0;
      if (it.lift === want) continue;
      it.lift += Math.sign(want - it.lift) * Math.min(Math.abs(want - it.lift), dt * 6);
      it.obj.position.set(it.base.x - 0.06 * it.lift, it.base.y, it.base.z);
    }
    if (this.hands.length < 2) return;
    const d = new Date();
    const m = d.getMinutes() + d.getSeconds() / 60, hr = (d.getHours() % 12) + m / 60;
    this.hands[0].rotation.z = -(hr / 12) * Math.PI * 2;
    this.hands[1].rotation.z = -(m / 60) * Math.PI * 2;
  }

  private paper(file: string, w: number, h: number, tint = ROOM): THREE.Mesh {
    const tex = this.loader.load(POSTERS + file);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ map: tex, color: tint }));
    return m;
  }

  private pin(parent: THREE.Object3D, x: number, y: number, color = 0xb0322a) {
    const p = new THREE.Mesh(new THREE.SphereGeometry(0.009, 8, 6), new THREE.MeshBasicMaterial({ color }));
    p.position.set(x, y, 0.006);
    parent.add(p);
  }

  private posters() {
    // the notice board (booth side, by the boarding area): this run's recall notice front and centre,
    // the robots on the books pinned round it
    const board = new THREE.Group();
    board.position.set(-CAR.hw + 0.155, 0, 0);
    board.rotation.y = Math.PI / 2; // faces the aisle (+X)
    this.group.add(board);
    const put = (file: string, w: number, h: number, z: number, y: number, tilt: number, pins = 1) => {
      const m = this.paper(file, w, h);
      // board-local: +X along the car is -Z in the rotated frame
      m.position.set(-z, y, 0.002 + Math.random() * 0.002);
      m.rotation.z = tilt;
      board.add(m);
      this.pin(m, 0, h / 2 - 0.025);
      if (pins > 1) this.pin(m, w / 2 - 0.03, -h / 2 + 0.03, 0xd8c070);
    };
    put('poster_recall_fresh.jpg', 0.36, 0.56, 5.4, 1.47, 0.02, 2);
    put('tintype_brute.jpg', 0.24, 0.24, 5.82, 1.7, -0.05);
    put('tintype_reactor_hulk.jpg', 0.24, 0.24, 5.9, 1.36, 0.04);
    put('poster_recall_form.jpg', 0.2, 0.31, 5.95, 1.07 + 0.155, -0.03);

    // Pop's poster: faded, behind the bar on the panel by his spot, name scratched out (§1.8)
    const old = this.paper('poster_recall_old.jpg', 0.3, 0.46, new THREE.Color(0.66, 0.56, 0.44));
    old.position.set(CAR.hw - 0.045, 1.5, -1.95);
    old.rotation.y = -Math.PI / 2;
    old.rotation.x = 0.01;
    this.group.add(old);
    this.pin(old, 0, 0.2, 0x6a5a3a);
  }

  private async fillRack() {
    const gl = Object.values(gunMods)[0], wl = Object.values(weaponMods)[0];
    type Gun = { id: number; mesh: THREE.Object3D; name: string; shine?: (k: number) => void };
    let longs: Gun[] = [];
    let pistols: Gun[] = [];
    if (gl && wl) {
      try {
        const [g, w] = await Promise.all([gl(), wl()]) as [GunsMod, WeaponsMod];
        const want = rackWeapons.ids?.() ?? null;
        // primaries and sidearms (slot 0/1); specials (rockets, fireworks) live in the crate, not the rack
        const defs = w.WEAPONS.filter((d) => (want ? want.includes(d.id) : d.slot <= 1));
        for (const d of defs) {
          const c = g.buildCaseWeapon?.(d.id, CASE_LIGHT);
          const e: Gun = { id: d.id, mesh: c ? c.obj : g.buildWeaponMesh(d.id), name: d.short || d.name, shine: c?.shine };
          (d.pistol ? pistols : longs).push(e);
        }
      } catch { longs = []; pistols = []; }
    }
    if (!longs.length && !pistols.length) {
      for (let i = 0; i < 4; i++) longs.push({ id: -1 - i, mesh: placeholderLongGun(i), name: '' });
    }
    longs = longs.slice(0, 4);
    pistols = pistols.slice(0, 5);
    // four long guns stood between the five sidearm columns, each under its own downlight
    longs.forEach((l, i) => this.stand(l, longZ(i + (4 - longs.length) / 2), true));
    pistols.forEach((p, i) => this.stand(p, pegZ(i + (5 - pistols.length) / 2), false));
    this.paintGlow();
  }

  /** Long guns stand muzzle-up in the trough; pistols lie along the board on their pegs, muzzle fore. */
  private stand(gun: { id: number; mesh: THREE.Object3D; name: string; shine?: (k: number) => void }, z: number, upright: boolean) {
    const { id, mesh, name } = gun;
    const box = new THREE.Box3().setFromObject(mesh);
    const size = box.getSize(new THREE.Vector3());
    const ax = size.x >= size.y && size.x >= size.z ? 0 : size.y >= size.z ? 1 : 2;
    const along = new THREE.Vector3().setComponent(ax, 1);
    const muzzle = mesh.userData.muzzle as THREE.Vector3 | undefined;
    if (muzzle && muzzle.getComponent(ax) < box.getCenter(new THREE.Vector3()).getComponent(ax)) along.negate();
    const pivot = new THREE.Group();
    const centre = box.getCenter(new THREE.Vector3());
    mesh.position.sub(centre);
    pivot.add(mesh);
    const holder = new THREE.Group();
    holder.add(pivot);
    pivot.quaternion.setFromUnitVectors(along, upright ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, -1));
    // the lane's guns are sized for a hand, not a rack: fit each to its slot (length and girth)
    const side = Math.max(...[0, 1, 2].filter((k) => k !== ax).map((k) => size.getComponent(k)));
    const fit = Math.min(1, (upright ? 0.92 : 0.4) / size.getComponent(ax), (upright ? 0.2 : 0.15) / side);
    pivot.scale.setScalar(fit);
    pivot.updateMatrixWorld(true);
    const fb = new THREE.Box3().setFromObject(pivot);
    const fs = fb.getSize(new THREE.Vector3());
    // upright: stood on the felt; pistols: resting on their two pegs
    if (upright) holder.position.set(LONG_X, TROUGH_Y + fs.y / 2 + 0.005, z);
    else holder.position.set(PEG_X, PEG_Y + 0.012 + fs.y / 2, z);
    this.rack.add(holder);
    const key = itemKey(upright ? 'long' : 'peg', id);
    this.items.set(key, { obj: holder, base: holder.position.clone(), lift: 0 });
    this.fixings(upright, z, fs, holder.position.y);
    // long guns: on the cabinet's front lip; pistols: on the back board under their pegs
    if (name) {
      const plate = nameplate(name, upright ? HW - 0.468 : BOARD_X - 0.004, upright ? 0.925 : PEG_Y - 0.08, z, upright ? 0.17 : 0.24);
      this.rack.add(plate);
      this.lit.set(key, { k: 0, plate: plate.material as THREE.MeshBasicMaterial, shine: gun.shine, light: this.lightAt(z) });
    }
  }

  /** brass pegs under a pistol; a felt-lined saddle at a long gun's foot and a brass clip up the board */
  private fixings(upright: boolean, z: number, fs: THREE.Vector3, y: number) {
    const brass = this.brassMat;
    if (!upright) {
      const tip = PEG_X - 0.02;
      const peg = new THREE.CylinderGeometry(0.0055, 0.0055, BOARD_X - tip, 8);
      peg.rotateZ(Math.PI / 2);
      for (const dz of [-0.07, 0.07]) {
        const m = new THREE.Mesh(peg, brass);
        m.position.set((BOARD_X + tip) / 2, PEG_Y, z + dz * Math.min(1, fs.z / 0.3));
        const knob = new THREE.Mesh(this.knobGeo, brass);
        knob.position.set(tip, PEG_Y + 0.003, m.position.z);
        this.rack.add(m, knob);
      }
      return;
    }
    const wood = this.saddleMat;
    for (const s of [-1, 1]) {
      const b = new THREE.Mesh(this.saddleGeo, wood);
      b.position.set(LONG_X, TROUGH_Y + 0.03, z + s * (fs.z / 2 + 0.014));
      this.rack.add(b);
    }
    // the clip holds the barrel to the board, two thirds up
    const cy = Math.min(y + fs.y * 0.28, TROUGH_Y + 0.8);
    const back = LONG_X + fs.x / 2 - 0.015;
    if (back < BOARD_X - 0.005) {
      const clip = new THREE.Mesh(new THREE.BoxGeometry(BOARD_X - back, 0.014, 0.03), brass);
      clip.position.set((BOARD_X + back) / 2, cy, z);
      this.rack.add(clip);
    }
  }

  // ---------------------------------------------------------------- the case: board, soffit, downlights
  private brassMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0x8a6630).multiply(ROOM) });
  private saddleMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0x2a1810).multiply(ROOM) });
  private saddleGeo = new THREE.BoxGeometry(0.11, 0.06, 0.022);
  private knobGeo = new THREE.SphereGeometry(0.009, 8, 6);
  private boardCtx: CanvasRenderingContext2D | null = null;
  private boardTex: THREE.CanvasTexture | null = null;
  private puckCol: THREE.BufferAttribute | null = null;
  private coneLit: THREE.BufferAttribute | null = null;
  private poolCol: THREE.BufferAttribute | null = null;
  /** z of every downlight, in order (one per long gun and per sidearm column) */
  private lights: number[] = [];
  private lightAt(z: number) { let best = 0; this.lights.forEach((l, i) => { if (Math.abs(l - z) < Math.abs(this.lights[best] - z)) best = i; }); return best; }

  private caseShell() {
    for (let i = 0; i < 5; i++) this.lights.push(pegZ(i));
    for (let i = 0; i < 4; i++) this.lights.push(longZ(i));
    // the back board: oxblood velvet in a brass-tacked border, painted with the downlights' scallops
    const c = document.createElement('canvas');
    c.width = 1024; c.height = 584;
    this.boardCtx = c.getContext('2d')!;
    this.boardTex = new THREE.CanvasTexture(c);
    this.boardTex.colorSpace = THREE.SRGBColorSpace;
    this.boardTex.anisotropy = 4;
    const bh = CROWN_Y - TROUGH_Y, bw = CZ1 - CZ0;
    const board = new THREE.Mesh(new THREE.PlaneGeometry(bw, bh), new THREE.MeshBasicMaterial({ map: this.boardTex }));
    board.position.set(BOARD_X, TROUGH_Y + bh / 2, (CZ0 + CZ1) / 2);
    board.rotation.y = -Math.PI / 2;
    this.rack.add(board);

    // the soffit under the crown, with the downlights let into it, and a brass fascia across the front
    const x0 = HW - 0.42 + 0.005;
    const soffit = new THREE.Mesh(new THREE.BoxGeometry(BOARD_X - x0, CROWN_Y - SOFFIT_Y, bw), new THREE.MeshBasicMaterial({ color: new THREE.Color(0x1c120c).multiply(ROOM) }));
    soffit.position.set((BOARD_X + x0) / 2, (SOFFIT_Y + CROWN_Y) / 2, (CZ0 + CZ1) / 2);
    const fascia = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.03, bw), this.brassMat);
    fascia.position.set(x0 - 0.007, SOFFIT_Y + 0.015, (CZ0 + CZ1) / 2);
    this.rack.add(soffit, fascia);

    const n = this.lights.length;
    const lens: THREE.BufferGeometry[] = [], trims: THREE.BufferGeometry[] = [], cones: THREE.BufferGeometry[] = [], pools: THREE.BufferGeometry[] = [];
    this.lights.forEach((z, i) => {
      const isLong = i >= 5;
      const l = new THREE.CircleGeometry(0.022, 20); l.rotateX(Math.PI / 2); l.translate(PUCK_X, SOFFIT_Y - 0.0015, z);
      const t = new THREE.RingGeometry(0.022, 0.034, 20); t.rotateX(Math.PI / 2); t.translate(PUCK_X, SOFFIT_Y - 0.001, z);
      // the beam: a soft open cone down to the felt (or to the sidearm and a little past)
      const h = isLong ? SOFFIT_Y - TROUGH_Y : 0.75;
      const cg = new THREE.CylinderGeometry(0.02, isLong ? 0.2 : 0.22, h, 24, 6, true);
      cg.translate(PUCK_X + 0.02, SOFFIT_Y - h / 2, z);
      const v = new Float32Array(cg.attributes.position.count);
      for (let k = 0; k < v.length; k++) v[k] = (SOFFIT_Y - cg.attributes.position.getY(k)) / h;
      cg.setAttribute('aV', new THREE.BufferAttribute(v, 1));
      cg.setAttribute('aLit', new THREE.BufferAttribute(new Float32Array(v.length).fill(1), 1));
      lens.push(l); trims.push(t); cones.push(cg);
      if (isLong) {
        const p = new THREE.PlaneGeometry(0.3, 0.26); p.rotateX(-Math.PI / 2); p.translate(LONG_X - 0.02, TROUGH_Y + 0.002, z);
        pools.push(p);
      }
    });
    const lensGeo = mergeGeometries(lens)!;
    this.puckCol = new THREE.BufferAttribute(new Float32Array(lensGeo.attributes.position.count * 3), 3);
    lensGeo.setAttribute('color', this.puckCol);
    this.rack.add(new THREE.Mesh(lensGeo, new THREE.MeshBasicMaterial({ vertexColors: true })));
    this.rack.add(new THREE.Mesh(mergeGeometries(trims)!, this.brassMat));
    const coneGeo = mergeGeometries(cones)!;
    this.coneLit = coneGeo.getAttribute('aLit') as THREE.BufferAttribute;
    const beam = new THREE.Mesh(coneGeo, beamMaterial());
    beam.renderOrder = 3;
    this.rack.add(beam);
    // the trough's felt, live: a nap and a darker falloff to its edges, under the pools
    const felt = new THREE.Mesh(new THREE.PlaneGeometry(bw, BOARD_X - (HW - 0.38)), new THREE.MeshBasicMaterial({ map: feltTex(), color: ROOM }));
    felt.rotation.order = 'YXZ';
    felt.rotation.set(-Math.PI / 2, Math.PI / 2, 0);
    felt.position.set((BOARD_X + HW - 0.38) / 2, TROUGH_Y + 0.001, (CZ0 + CZ1) / 2);
    this.rack.add(felt);
    const poolGeo = mergeGeometries(pools)!;
    this.poolCol = new THREE.BufferAttribute(new Float32Array(poolGeo.attributes.position.count * 3), 3);
    poolGeo.setAttribute('color', this.poolCol);
    const pool = new THREE.Mesh(poolGeo, new THREE.MeshBasicMaterial({ map: radialTex(), vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    pool.renderOrder = 2;
    this.rack.add(pool);
    this.paintGlow();
  }

  // ---------------------------------------------------------------- what's equipped
  /** per rack item: its equipped glow (0..1, eased), its nameplate, the gun's shine and its downlight */
  private lit = new Map<string, { k: number; plate?: THREE.MeshBasicMaterial; shine?: (k: number) => void; light?: number; mats?: THREE.MeshBasicMaterial[]; halo?: THREE.Mesh }>();
  private want = new Set<string>();
  private t = 0;
  private dirty = true;

  /** What the local cowboy has on: those sit in a brighter, warmer light on the rack. */
  setEquipped(cls: number, guns: number[], nade: number) {
    const keys = [itemKey('cls', cls), itemKey('nade', nade)];
    for (const g of guns) keys.push(this.items.has(itemKey('long', g)) ? itemKey('long', g) : itemKey('peg', g));
    if (keys.length === this.want.size && keys.every((k) => this.want.has(k))) return;
    this.want = new Set(keys);
    this.dirty = true;
  }

  private glow(dt: number) {
    let moving = this.dirty;
    this.dirty = false;
    const pulse = 0.82 + 0.18 * Math.sin(this.t * 2.4);
    for (const [key, l] of this.lit) {
      const w = this.want.has(key) ? 1 : 0;
      if (l.k !== w) { l.k += Math.sign(w - l.k) * Math.min(Math.abs(w - l.k), dt * 2.5); moving = true; }
      if (l.k <= 0) continue;
      const k = l.k * pulse;
      l.shine?.(k);
      if (l.plate) l.plate.color.copy(ROOM).multiplyScalar(1 + 2.2 * k);
      if (l.mats) for (const m of l.mats) m.color.copy(ROOM).multiplyScalar(1 + 0.9 * k);
      if (l.halo) { l.halo.visible = true; (l.halo.material as THREE.MeshBasicMaterial).opacity = 0.75 * k; }
    }
    for (const l of this.lit.values()) if (l.k <= 0) { if (l.halo) l.halo.visible = false; l.plate?.color.copy(ROOM); if (l.mats) for (const m of l.mats) m.color.copy(ROOM); }
    if (moving) this.paintGlow();
  }

  /** the downlights: every lens, beam and pool; the equipped ones hotter and golder */
  private paintGlow() {
    const lk = new Float32Array(this.lights.length);
    for (const l of this.lit.values()) if (l.light !== undefined) lk[l.light] = Math.max(lk[l.light], l.k);
    const c = new THREE.Color();
    if (this.puckCol) {
      const per = this.puckCol.count / this.lights.length;
      for (let i = 0; i < this.lights.length; i++) {
        c.copy(WARM).lerp(GOLD, lk[i]).multiplyScalar(4.5 + 5 * lk[i]);
        for (let v = 0; v < per; v++) this.puckCol.setXYZ(i * per + v, c.r, c.g, c.b);
      }
      this.puckCol.needsUpdate = true;
    }
    if (this.coneLit) {
      const per = this.coneLit.count / this.lights.length;
      for (let i = 0; i < this.lights.length; i++) for (let v = 0; v < per; v++) this.coneLit.setX(i * per + v, 1 + 2.4 * lk[i]);
      this.coneLit.needsUpdate = true;
    }
    if (this.poolCol) {
      const per = this.poolCol.count / 4;
      for (let i = 0; i < 4; i++) {
        c.copy(WARM).lerp(GOLD, lk[5 + i]).multiplyScalar(0.3 + 0.7 * lk[5 + i]);
        for (let v = 0; v < per; v++) this.poolCol.setXYZ(i * per + v, c.r, c.g, c.b);
      }
      this.poolCol.needsUpdate = true;
    }
    this.paintBoard(lk);
  }

  private boardKey = '';
  private paintBoard(lk: Float32Array) {
    const g = this.boardCtx, tex = this.boardTex;
    if (!g || !tex) return;
    const key = Array.from(lk, (v) => Math.round(v * 8)).join();
    if (key === this.boardKey) return;
    this.boardKey = key;
    const W = g.canvas.width, H = g.canvas.height;
    const bw = CZ1 - CZ0, bh = CROWN_Y - TROUGH_Y;
    const px = (z: number) => ((z - CZ0) / bw) * W, py = (y: number) => ((CROWN_Y - y) / bh) * H;
    // velvet: deep oxblood, darker to the foot, a fine vertical nap
    const base = g.createLinearGradient(0, 0, 0, H);
    base.addColorStop(0, '#26090a'); base.addColorStop(1, '#0c0303');
    g.globalCompositeOperation = 'source-over';
    g.fillStyle = base; g.fillRect(0, 0, W, H);
    if (!this.nap) this.nap = napPattern(g);
    g.fillStyle = this.nap; g.fillRect(0, 0, W, H);
    // the shadow under the soffit
    const sh = g.createLinearGradient(0, 0, 0, py(SOFFIT_Y - 0.12));
    sh.addColorStop(0, 'rgba(0,0,0,0.75)'); sh.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = sh; g.fillRect(0, 0, W, py(SOFFIT_Y - 0.12));
    // the scallops: each downlight grazes the board from just above, brightest under the lens
    g.globalCompositeOperation = 'lighter';
    this.lights.forEach((z, i) => {
      const k = lk[i];
      const x = px(z), y0 = py(SOFFIT_Y);
      const r = W * 0.075;
      g.save();
      g.translate(x, y0 + r * 0.25);
      g.scale(1, i >= 5 ? 4.6 : 2.4);
      const gr = g.createRadialGradient(0, 0, 0, 0, 0, r);
      const a = 0.38 + 0.5 * k;
      const col = k > 0.5 ? '255,176,92' : '255,196,128';
      gr.addColorStop(0, `rgba(${col},${a})`); gr.addColorStop(0.35, `rgba(${col},${a * 0.45})`); gr.addColorStop(1, `rgba(${col},0)`);
      g.fillStyle = gr;
      g.beginPath(); g.arc(0, 0, r, 0, Math.PI); g.fill();
      g.restore();
    });
    g.globalCompositeOperation = 'source-over';
    // brass-tacked border
    g.strokeStyle = 'rgba(176,132,64,0.9)'; g.lineWidth = 3; g.strokeRect(14, py(SOFFIT_Y) + 8, W - 28, H - py(SOFFIT_Y) - 20);
    for (let x = 24; x < W - 14; x += 26) for (const y of [py(SOFFIT_Y) + 8, H - 12]) tack(g, x, y);
    for (let y = py(SOFFIT_Y) + 34; y < H - 20; y += 26) for (const x of [14, W - 14]) tack(g, x, y);
    tex.needsUpdate = true;
  }
  private nap: CanvasPattern | null = null;

  /** The grenades ride a leather bandolier along the cabinet front, one of each, element-banded. */
  private bandolier() {
    const x = CAR.hw - 0.42;
    const strap = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.05, CAR.rackHalf * 2 - 0.12), new THREE.MeshStandardMaterial({ color: 0x3a2214, roughness: 0.8 }));
    strap.position.set(x - 0.006, NADE_Y, CAR.rackZ);
    this.rack.add(strap);
    const shell = new THREE.IcosahedronGeometry(0.052, 1);
    const band = new THREE.TorusGeometry(0.052, 0.009, 6, 18);
    const iron = new THREE.MeshStandardMaterial({ color: 0x2c2b29, roughness: 0.45, metalness: 0.75 });
    for (let i = 0; i < NADE_COLOR.length; i++) {
      const g = new THREE.Group();
      g.add(new THREE.Mesh(shell, iron));
      const bm = new THREE.MeshBasicMaterial({ color: new THREE.Color(NADE_COLOR[i]).multiply(ROOM).multiplyScalar(0.62) });
      const b = new THREE.Mesh(band, bm);
      b.rotation.y = Math.PI / 2;
      g.add(b);
      const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.018, 0.03, 8), iron);
      cap.position.y = 0.058;
      g.add(cap);
      g.position.set(x - 0.05, NADE_Y + 0.005, nadeZ(i));
      this.rack.add(g);
      this.items.set(itemKey('nade', i), { obj: g, base: g.position.clone(), lift: 0 });
      const halo = haloRing(0.075, NADE_COLOR[i]);
      halo.position.set(x - 0.016, NADE_Y + 0.005, nadeZ(i));
      this.rack.add(halo);
      this.lit.set(itemKey('nade', i), { k: 0, halo, mats: [] });
      void bm;
    }
  }

  /** Four brass class badges on the rack's sign board: the emblems the rack readout shows. */
  private plaques() {
    const x = CAR.hw - 0.42 + 0.015;
    for (let i = 0; i < 4; i++) {
      const c = document.createElement('canvas');
      c.width = c.height = 128;
      const g = c.getContext('2d')!;
      const grad = g.createRadialGradient(54, 50, 6, 64, 64, 64);
      grad.addColorStop(0, '#f0d08a'); grad.addColorStop(0.7, '#b8893e'); grad.addColorStop(1, '#6e4a1c');
      g.fillStyle = grad; g.beginPath(); g.arc(64, 64, 62, 0, Math.PI * 2); g.fill();
      g.strokeStyle = '#4a3010'; g.lineWidth = 4; g.beginPath(); g.arc(64, 64, 57, 0, Math.PI * 2); g.stroke();
      g.strokeStyle = '#2a1a08';
      drawEmblem(g, i, 64, 64, 74, 6);
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      const m = new THREE.Mesh(new THREE.CircleGeometry(0.1, 32), new THREE.MeshBasicMaterial({ map: tex, color: ROOM }));
      m.position.set(x - 0.008, 2.63, CAR.rackZ - 0.375 + i * 0.25);
      m.rotation.y = -Math.PI / 2;
      this.rack.add(m);
      this.items.set(itemKey('cls', i), { obj: m, base: m.position.clone(), lift: 0 });
      const halo = haloRing(0.13, 0xffb45a);
      halo.position.set(x - 0.004, 2.63, m.position.z);
      this.rack.add(halo);
      this.lit.set(itemKey('cls', i), { k: 0, halo, mats: [m.material as THREE.MeshBasicMaterial] });
    }
  }

  private items = new Map<string, { obj: THREE.Object3D; base: THREE.Vector3; lift: number }>();
  private focusKey = '';

  /**
   * The rack readout's current item, car-local: the object (for its bounds) or null when it isn't on
   * the rack. `kind`: 'cls' class badge, 'gun' weapon id, 'nade' grenade id.
   */
  rackItem(kind: 'cls' | 'gun' | 'nade', id: number): THREE.Object3D | null {
    const k = kind === 'gun' ? (this.items.has(itemKey('long', id)) ? itemKey('long', id) : itemKey('peg', id)) : itemKey(kind, id);
    return this.items.get(k)?.obj ?? null;
  }
  /** lift the browsed item off the rack toward the aisle (null: everything back in place) */
  focus(kind: 'cls' | 'gun' | 'nade' | null, id = 0) {
    if (!kind) { this.focusKey = ''; return; }
    this.focusKey = kind === 'gun' ? (this.items.has(itemKey('long', id)) ? itemKey('long', id) : itemKey('peg', id)) : itemKey(kind, id);
  }
}

function placeholderLongGun(i: number): THREE.Object3D {
  const g = new THREE.Group();
  const wood = new THREE.MeshStandardMaterial({ color: 0x5a3018, roughness: 0.5 });
  const iron = new THREE.MeshStandardMaterial({ color: 0x2a2826, roughness: 0.4, metalness: 0.8 });
  const L = 0.85 + (i % 3) * 0.1;
  const stock = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.1, 0.3), wood); stock.position.z = 0.15;
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.07, 0.2), iron); body.position.z = -0.08;
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, L * 0.55, 8), iron);
  barrel.rotation.x = Math.PI / 2; barrel.position.z = -0.18 - L * 0.275; barrel.position.y = 0.02;
  g.add(stock, body, barrel);
  g.userData.muzzle = new THREE.Vector3(0, 0.02, -0.18 - L * 0.55);
  return g;
}

function nameplate(text: string, x: number, y: number, z: number, w = 0.17): THREE.Mesh {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 48;
  const g = c.getContext('2d')!;
  const grad = g.createLinearGradient(0, 0, 0, 48);
  grad.addColorStop(0, '#d9b46a'); grad.addColorStop(0.5, '#a97a34'); grad.addColorStop(1, '#7a5420');
  g.fillStyle = grad; g.fillRect(0, 0, 256, 48);
  g.strokeStyle = '#4a3010'; g.lineWidth = 3; g.strokeRect(3, 3, 250, 42);
  g.fillStyle = '#2a1a08'; g.font = 'bold 26px Georgia, serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText(text.toUpperCase().slice(0, 16), 128, 25);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, w * 0.188), new THREE.MeshBasicMaterial({ map: tex, color: ROOM }));
  m.position.set(x, y, z);
  m.rotation.y = -Math.PI / 2; // faces the aisle (-X)
  return m;
}

/** The downlight's beam: a soft additive cone, brightest under the lens and along its axis. */
function beamMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    uniforms: { uCol: { value: new THREE.Color(1.0, 0.62, 0.3) } },
    vertexShader: /* glsl */ `
      attribute float aV; attribute float aLit;
      varying float vV; varying float vLit; varying float vEdge;
      void main() {
        vV = aV; vLit = aLit;
        vec4 mv = modelViewMatrix * vec4( position, 1.0 );
        vec3 n = normalize( normalMatrix * normal );
        vEdge = abs( dot( n, normalize( -mv.xyz ) ) );
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 uCol;
      varying float vV; varying float vLit; varying float vEdge;
      void main() {
        float a = pow( 1.0 - vV, 1.8 ) * smoothstep( 0.0, 0.08, vV ) * pow( vEdge, 1.6 ) * 0.055 * vLit;
        gl_FragColor = vec4( uCol * a, 1.0 );
      }`,
  });
}

let radial: THREE.Texture | null = null;
function radialTex(): THREE.Texture {
  if (radial) return radial;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.5, 'rgba(255,255,255,0.4)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
  radial = new THREE.CanvasTexture(c);
  return radial;
}

/** a warm ring of light behind an equipped badge or grenade, facing the aisle */
function haloRing(r: number, color: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.PlaneGeometry(r * 2, r * 2), new THREE.MeshBasicMaterial({
    map: ringTex(), color: new THREE.Color(color).multiplyScalar(2.2), transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false,
  }));
  m.rotation.y = -Math.PI / 2;
  m.visible = false;
  m.renderOrder = 2;
  return m;
}
let ring: THREE.Texture | null = null;
function ringTex(): THREE.Texture {
  if (ring) return ring;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const gr = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  gr.addColorStop(0, 'rgba(255,255,255,0)'); gr.addColorStop(0.62, 'rgba(255,255,255,0.05)');
  gr.addColorStop(0.8, 'rgba(255,255,255,0.9)'); gr.addColorStop(0.88, 'rgba(255,255,255,0.35)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 128, 128);
  ring = new THREE.CanvasTexture(c);
  return ring;
}

/** velvet nap: fine vertical streaks, darker and lighter, tiled */
function napPattern(g: CanvasRenderingContext2D): CanvasPattern {
  const c = document.createElement('canvas');
  c.width = 128; c.height = 128;
  const n = c.getContext('2d')!;
  let seed = 11;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 260; i++) {
    const x = rnd() * 128, h = 20 + rnd() * 90, y = rnd() * 128;
    n.fillStyle = rnd() < 0.5 ? `rgba(0,0,0,${(0.08 + rnd() * 0.12).toFixed(3)})` : `rgba(255,170,150,${(0.02 + rnd() * 0.04).toFixed(3)})`;
    n.fillRect(x, y, 1, h); n.fillRect(x, y - 128, 1, h);
  }
  return g.createPattern(c, 'repeat')!;
}

function tack(g: CanvasRenderingContext2D, x: number, y: number) {
  const gr = g.createRadialGradient(x - 1.5, y - 1.5, 0.5, x, y, 5);
  gr.addColorStop(0, '#f6d896'); gr.addColorStop(0.6, '#a87a36'); gr.addColorStop(1, '#3a2408');
  g.fillStyle = gr; g.beginPath(); g.arc(x, y, 4.5, 0, Math.PI * 2); g.fill();
}

/** baize: deep green with a fine nap, falling off darker toward the edges */
function feltTex(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 128;
  const g = c.getContext('2d')!;
  g.fillStyle = '#1d3a24'; g.fillRect(0, 0, 512, 128);
  let seed = 5;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 5000; i++) {
    g.fillStyle = rnd() < 0.5 ? `rgba(0,0,0,${(0.05 + rnd() * 0.1).toFixed(3)})` : `rgba(140,190,140,${(0.03 + rnd() * 0.05).toFixed(3)})`;
    g.fillRect(rnd() * 512, rnd() * 128, 1 + rnd() * 2, 1);
  }
  const v = g.createLinearGradient(0, 0, 0, 128);
  v.addColorStop(0, 'rgba(0,0,0,0.55)'); v.addColorStop(0.3, 'rgba(0,0,0,0)'); v.addColorStop(0.7, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,0.55)');
  g.fillStyle = v; g.fillRect(0, 0, 512, 128);
  const h = g.createLinearGradient(0, 0, 512, 0);
  h.addColorStop(0, 'rgba(0,0,0,0.5)'); h.addColorStop(0.06, 'rgba(0,0,0,0)'); h.addColorStop(0.94, 'rgba(0,0,0,0)'); h.addColorStop(1, 'rgba(0,0,0,0.5)');
  g.fillStyle = h; g.fillRect(0, 0, 512, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
